import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { IDockviewPanelProps } from 'dockview-react';
import { api } from '../lib/api';
import { errorMessage, short, usd, when } from '../lib/format';
import { Conversation } from '../routes/chat';
import { fmt, useAccount, useConnectors, useOpenOrders, useRecentTrades } from './market';

/** Native tiles besides the chart (D10): account/positions, open orders, recent fills, jobs, credits, chat. */

function useDefaultConnector(named?: string) {
  const connectors = useConnectors();
  return {
    connector: named ?? connectors.data?.connectors.find((c) => c.status === 'active')?.name,
    loading: connectors.isLoading,
  };
}

function Status({
  loading,
  error,
  empty,
}: {
  loading: boolean;
  error: unknown;
  empty?: string | undefined;
}) {
  if (loading) return <span className="muted">loading…</span>;
  if (error) return <span className="error">{errorMessage(error)}</span>;
  if (empty) return <span className="muted">{empty}</span>;
  return null;
}

function NoConnector() {
  return (
    <div className="tile pad">
      <span className="muted">
        No active connector. <Link to="/connectors">Add one</Link> to see exchange data here.
      </span>
    </div>
  );
}

export function AccountTile({ params }: IDockviewPanelProps<{ connector?: string }>) {
  const { connector, loading } = useDefaultConnector(params.connector);
  const q = useAccount(connector);
  if (!connector && !loading) return <NoConnector />;
  const a = q.data;
  return (
    <div className="tile pad">
      {a && (
        <div className="kpis">
          <div>
            <span className="muted">Account value</span>
            <strong>{fmt.usd(a.account_value)}</strong>
          </div>
          <div>
            <span className="muted">Unrealized PnL</span>
            <strong className={a.total_unrealized_pnl >= 0 ? 'ok' : 'error'}>
              {fmt.usd(a.total_unrealized_pnl)}
            </strong>
          </div>
          <div>
            <span className="muted">Margin used</span>
            <strong>{fmt.usd(a.total_margin_used)}</strong>
          </div>
          <div>
            <span className="muted">Withdrawable</span>
            <strong>{fmt.usd(a.withdrawable)}</strong>
          </div>
        </div>
      )}
      <Status
        loading={q.isLoading}
        error={q.error}
        empty={a && a.positions.length === 0 ? 'no open positions' : undefined}
      />
      {a && a.positions.length > 0 && (
        <table className="compact">
          <thead>
            <tr>
              <th>Coin</th>
              <th>Size</th>
              <th>Entry</th>
              <th>Value</th>
              <th>uPnL</th>
              <th>Lev</th>
              <th>Liq</th>
            </tr>
          </thead>
          <tbody>
            {a.positions.map((p) => (
              <tr key={p.coin}>
                <td>{p.coin}</td>
                <td className={p.size >= 0 ? 'ok' : 'error'}>{fmt.qty(p.size)}</td>
                <td>{fmt.px(p.entry_px)}</td>
                <td>{fmt.usd(p.position_value)}</td>
                <td className={p.unrealized_pnl >= 0 ? 'ok' : 'error'}>
                  {fmt.usd(p.unrealized_pnl)}
                </td>
                <td>{p.leverage}x</td>
                <td>{p.liquidation_px === null ? '—' : fmt.px(p.liquidation_px)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function OrdersTile({ params }: IDockviewPanelProps<{ connector?: string }>) {
  const { connector, loading } = useDefaultConnector(params.connector);
  const q = useOpenOrders(connector);
  if (!connector && !loading) return <NoConnector />;
  return (
    <div className="tile pad">
      <Status
        loading={q.isLoading}
        error={q.error}
        empty={q.data?.length === 0 ? 'no open orders' : undefined}
      />
      {q.data && q.data.length > 0 && (
        <table className="compact">
          <thead>
            <tr>
              <th>Coin</th>
              <th>Side</th>
              <th>Type</th>
              <th>Price</th>
              <th>Size</th>
              <th>Orig</th>
              <th>oid</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((o) => (
              <tr key={`${o.oid ?? `${o.coin}-${o.limit_px}-${o.size}`}`}>
                <td>{o.coin}</td>
                <td className={/buy|long|bid|^b$/i.test(o.side) ? 'ok' : 'error'}>{o.side}</td>
                <td>{o.order_type}</td>
                <td>{fmt.px(o.limit_px ?? 0)}</td>
                <td>{fmt.qty(o.size ?? 0)}</td>
                <td>{fmt.qty(o.original_size ?? 0)}</td>
                <td className="muted">{String(o.oid ?? '')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function TradesTile({ params }: IDockviewPanelProps<{ connector?: string }>) {
  const { connector, loading } = useDefaultConnector(params.connector);
  const q = useRecentTrades(connector);
  if (!connector && !loading) return <NoConnector />;
  return (
    <div className="tile pad">
      <Status
        loading={q.isLoading}
        error={q.error}
        empty={q.data?.length === 0 ? 'no fills yet' : undefined}
      />
      {q.data && q.data.length > 0 && (
        <table className="compact">
          <thead>
            <tr>
              <th>Time</th>
              <th>Coin</th>
              <th>Side</th>
              <th>Size</th>
              <th>Price</th>
              <th>PnL</th>
              <th>Fee</th>
            </tr>
          </thead>
          <tbody>
            {q.data.map((t) => (
              <tr key={`${t.oid ?? 'fill'}-${t.time}-${t.size}-${t.price}`}>
                <td className="muted">{fmt.time(t.time)}</td>
                <td>{t.coin}</td>
                <td className={/buy|long/i.test(t.dir ?? t.side) ? 'ok' : 'error'}>
                  {t.dir ?? t.side}
                </td>
                <td>{fmt.qty(t.size)}</td>
                <td>{fmt.px(t.price)}</td>
                <td className={t.closed_pnl >= 0 ? 'ok' : 'error'}>{fmt.usd(t.closed_pnl)}</td>
                <td className="muted">{fmt.usd(t.fee)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function JobsTile() {
  const jobs = useQuery({ queryKey: ['jobs'], queryFn: api.jobs, refetchInterval: 10_000 });
  return (
    <div className="tile pad">
      <Status
        loading={jobs.isLoading}
        error={jobs.error}
        empty={jobs.data?.jobs.length === 0 ? 'no jobs yet' : undefined}
      />
      {jobs.data && jobs.data.jobs.length > 0 && (
        <table className="compact">
          <thead>
            <tr>
              <th>Status</th>
              <th>Kind</th>
              <th>Command</th>
              <th>Started</th>
            </tr>
          </thead>
          <tbody>
            {jobs.data.jobs.slice(0, 50).map((j) => (
              <tr key={j.id}>
                <td>
                  <Link
                    to="/jobs/$jobId"
                    params={{ jobId: j.id }}
                    className={`pill ${j.status === 'running' ? 'live' : j.status === 'failed' || j.status === 'stuck' ? 'danger' : ''}`}
                  >
                    {j.status}
                  </Link>
                </td>
                <td className="muted">{j.kind}</td>
                <td>
                  <code>{short(j.spec.command, 60)}</code>
                </td>
                <td className="muted">{when(j.createdAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function CreditsTile() {
  const credits = useQuery({
    queryKey: ['credits'],
    queryFn: api.credits,
    refetchInterval: 15_000,
  });
  const usage = useQuery({
    queryKey: ['usage', 24],
    queryFn: () => api.usage(24),
    refetchInterval: 60_000,
  });
  const ledger = useQuery({
    queryKey: ['ledger', 20],
    queryFn: () => api.ledger(20),
    refetchInterval: 30_000,
  });
  const c = credits.data;
  return (
    <div className="tile pad">
      {c && (
        <div className="kpis">
          <div>
            <span className="muted">Available</span>
            <strong className={c.available < 100_000 ? 'error' : ''}>{usd(c.available, 2)}</strong>
          </div>
          <div>
            <span className="muted">Open holds</span>
            <strong>{usd(c.openHolds, 2)}</strong>
          </div>
          {usage.data && (
            <div>
              <span className="muted">VM 24 h</span>
              <strong>
                {usd(usage.data.chargedMicroUsd.vm_cpu + usage.data.chargedMicroUsd.vm_memory, 2)}
              </strong>
            </div>
          )}
          <div>
            <Link to="/account" className="pill">
              Buy credits
            </Link>
          </div>
        </div>
      )}
      <Status loading={credits.isLoading} error={credits.error} />
      {ledger.data && (
        <table className="compact">
          <tbody>
            {ledger.data.entries.slice(0, 12).map((e) => (
              <tr key={e.id}>
                <td className="muted">{when(e.at)}</td>
                <td>{e.kind}</td>
                <td className="muted">{e.meter}</td>
                <td className={e.kind === 'credit' ? 'ok' : ''}>{usd(e.amount, 4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ChatTile({ api: panel, params }: IDockviewPanelProps<{ threadId?: string }>) {
  const threads = useQuery({
    queryKey: ['threads'],
    queryFn: api.threads,
    refetchInterval: 15_000,
  });
  const threadId = params.threadId ?? threads.data?.threads[0]?.id;
  return (
    <div className="tile chat-tile">
      <div className="tile-bar">
        <select
          aria-label="Thread"
          value={threadId ?? ''}
          onChange={(e) => panel.updateParameters({ threadId: e.target.value })}
          style={{ width: 'auto', maxWidth: 280 }}
        >
          {threads.data?.threads.map((t) => (
            <option key={t.id} value={t.id}>
              {short(t.title ?? 'untitled', 40)}
            </option>
          ))}
        </select>
        <span className="grow" />
        <Link to="/chat" className="pill">
          New thread
        </Link>
      </div>
      {threadId ? (
        <Conversation key={threadId} threadId={threadId} />
      ) : (
        <div className="pad muted">
          No threads yet. <Link to="/chat">Start one</Link>.
        </div>
      )}
    </div>
  );
}
